import { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { productsApi, categoriesApi } from '../api/client';
import { useT, useLanguage, localise } from '../lib/i18n';
import ProductCard from '../components/ProductCard';
import { usePricingContext } from '../lib/PricingContext';
import { useAdminPreview } from '../lib/AdminPreviewContext';
import AdminPreviewToggle from '../components/AdminPreviewToggle';
import { useAuth } from '../lib/AuthContext';

export default function Catalog() {
  const t = useT();
  const { locale } = useLanguage();
  const { isB2B } = usePricingContext();
  const { user } = useAuth();
  const { previewMode } = useAdminPreview();
  const isAdmin = user?.role === 'admin';
  const effectiveIsB2B = isAdmin ? previewMode === 'b2b' : isB2B;

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    Promise.all([
      productsApi.list({}).then(r => r.data),
      categoriesApi.list().then(r => r.data),
    ]).then(([prods, cats]) => {
      setProducts(prods);
      setCategories(cats);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = products
    .filter(p => {
      const name = localise(p, 'name', locale).toLowerCase();
      const desc = localise(p, 'description', locale).toLowerCase();
      const matchSearch = !search || name.includes(search.toLowerCase()) || desc.includes(search.toLowerCase());
      const matchCat = !activeCategory || p.category === activeCategory;
      return matchSearch && matchCat;
    })
    .sort((a, b) => (a.name_nl ?? '').localeCompare(b.name_nl ?? '', 'nl', { sensitivity: 'base' }));

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="catalog-page">
      <div className="mb-12">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2 block">Collectie</span>
            <h1 className="font-serif text-4xl md:text-5xl font-light text-foreground">{t.catalog.title}</h1>
          </div>
          {isAdmin && <AdminPreviewToggle />}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-10">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            data-testid="catalog-search"
            type="text"
            placeholder={t.catalog.search}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-full border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-shadow"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="cat-all"
            onClick={() => setActiveCategory(null)}
            className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 ${!activeCategory ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-card border border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'}`}
          >
            {t.catalog.all}
          </button>
          {categories.map(cat => (
            <button
              key={cat._id}
              data-testid={`cat-${cat.slug}`}
              onClick={() => setActiveCategory(activeCategory === cat._id ? null : cat._id)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 ${activeCategory === cat._id ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-card border border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'}`}
            >
              {localise(cat, 'name', locale)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={`catalog-skeleton-${i}`} className="animate-pulse">
              <div className="aspect-square bg-muted rounded-2xl mb-3" />
              <div className="h-4 bg-muted rounded w-3/4 mb-2" />
              <div className="h-3 bg-muted rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 text-muted-foreground">
          <p className="font-serif text-2xl mb-2">{t.catalog.noResults}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
          {filtered.map(p => <ProductCard key={p._id} product={p} isB2B={effectiveIsB2B} />)}
        </div>
      )}
    </div>
  );
}
